<template>
  <a-modal
    :visible="props.open"
    :width="!handlingIntegration ? '640px': '980px'"
    centered
    :footer="false"
    @cancel="handleCancel"
    :bodyStyle="{
      height: '656px',
      overflow: 'auto'
    }"
    wrapClassName="no-padding"
  >
    <div v-if="!handlingIntegration" class="whatsapp-auto-setup">
      <div class="image-wrapper">
        <img src="@/app/channels/components/image/whatsapp_users.png" alt="whatsapp-user-image" />
      </div>
      <h1 class="title">Acércate a tus clientes con una<br />cuenta de WhatsApp Business</h1>
      <p class="description">
        Integra tu cuenta de WhatsApp Business a través de 2 pasos: <br/>
        <strong>Crear WhatsApp Business y Configurar en Facebook.</strong>
      </p>
      <p class="note">Antes de iniciar, considera lo siguiente:</p>

      <div class="info-list">
        <div class="info-item" v-for="(item, i) in items" :key="i">
          <component :is="item.icon" class="icon" />
          <span class="info-text" v-html="item.text"></span>
        </div>
      </div>
      <div class="alert-wrapper">
        <a-alert type="info" show-icon>
          <template #icon>
            <VideoLibraryIcon />
          </template>
          <template #message>
            Aprende a integrar tu cuenta en pocos minutos
          </template>
          <template #description>
            Mira nuestro tutorial con el paso a paso.
            <a href="https://www.youtube.com/watch?v=Zu-ilRogMYI" target="_blank" rel="noopener noreferrer">
              <span style="display: inline-flex; gap: 2px; align-items: center;">
                Ver videotutorial
                <IconExternalLink color="currentColor" style="width: 14px; " />
              </span>
            </a>
          </template>
        </a-alert>
      </div>
      <div class="footer">
        <a-button class="button" type="primary" @click="handleOk">
          Integrar WhatsApp Business
        </a-button>
      </div>
    </div>
    <div class="whatsapp-business-integration" v-else>
      <WhatsappBusinessIntegration @cancel="handleCancel" @close="handleClose" ref="whatsappIntegrationRef"/>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import WhatsappBusinessIntegration from "@/app/channels/components/modals/whatsapp/WhatsappBusinessIntegration.vue";
import MessageIcon from "@/app/common/components/icons/MessageIcon.vue";
import FacebookIcon from "@/app/common/components/icons/FacebookIcon.vue";
import PhoneIcon from "@/app/common/components/icons/PhoneIcon.vue";
import TimeIcon from "@/app/common/components/icons/TimeIcon.vue";
import VideoLibraryIcon from "@/app/common/components/icons/VideoLibraryIcon.vue";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import { ref, createVNode } from 'vue';
import { Modal } from 'ant-design-vue';
import { ExclamationCircleOutlined } from "@ant-design/icons-vue";

const handlingIntegration = ref(false);
const whatsappIntegrationRef = ref();

const props = defineProps<{
  open: boolean;
}>();

const items = [
  { text: "Ten a la mano los datos de contacto del administrador de la cuenta de WhatsApp Business.", icon: MessageIcon},
  { text: "El proceso en Facebook Business Manager solo se podrá realizar desde la cuenta de su administrador.", icon: FacebookIcon},
  { text: "Verifica que el número a ser integrado no esté vinculado a otra cuenta de WhatsApp Business o personal.", icon: PhoneIcon},
  { text: "Meta dispone de <strong style='font-weight: 600'>máximo 72 horas</strong> para aprobar y activar tu cuenta.", icon: TimeIcon},
];

const emit = defineEmits<{
  (e: "close"): void;
}>();

const handleCancel = () => {
  const isDirty = whatsappIntegrationRef.value?.isDirty();
  if (isDirty) {
    whatsappIntegrationRef.value?.showModal();
  }
  else {
    whatsappIntegrationRef.value?.resetForm();
    emit("close");
  }
};

const handleClose = () => {
  whatsappIntegrationRef.value?.resetForm();
  emit("close");
};

const handleOk = () => {
  const confirmationModal = Modal.confirm({
    title: '¿Todo listo para integrar?',
    content: createVNode('span', {}, [
      'Recuerda que si tienes dudas, puedes acceder a nuestro ',
      createVNode('strong', {}, 'videotutorial'),
      ' ahora o durante el proceso.',
    ]),
    icon: createVNode(ExclamationCircleOutlined),
    onOk: () => {
      confirmationModal.destroy();
      handlingIntegration.value = true;
    },
    onCancel: () => {
      window.open("https://www.youtube.com/watch?v=Zu-ilRogMYI", "_blank", "noopener,noreferrer");
      confirmationModal.destroy();
      handlingIntegration.value = true;
    },
    centered: true,
    okText: "Iniciar integración",
    cancelText: createVNode('span', {
      style: { alignItems: 'center', display: 'inline-flex', gap: '8px' },
    }, [
      'Ver videotutorial',
      createVNode(IconExternalLink, {
        class: 'external-link-icon',
        color: 'currentColor',
      }),
    ]),
  })
};
</script>
<style lang="sass" scoped>

:global(.no-padding .ant-modal-content)
  padding: 0

.whatsapp-auto-setup
  border-radius: 12px
  background: linear-gradient(180deg, #F7FFF0 0%, #FFFFFF 27%)
  display: flex
  flex-direction: column
  align-items: center
  text-align: center
  padding: 32px 80px

  font-size: 14px
  line-height: 22px
  letter-spacing: 0

  :deep(.ant-steps-item)
    pointer-events: none

  :deep(.steps .ant-steps-item-title)
    display: block
    width: max-content
    margin-left: 50%
    transform: translateX(-50%)
    white-space: nowrap

  .image-wrapper
    text-align: center
    margin-bottom: 16px
    img
      max-width: 80px

  .title
    font-size: 24px
    line-height: 32px
    font-weight: 600
    color: #000000E0

  .description
    font-weight: 400
    margin-bottom: 20px
    color: #000000A6

    strong
      font-weight: 600

  .steps
    width: 65%
    :deep(.ant-steps-item-title)
      font-weight: 600
      font-size: 14px
      line-height: 22px
      color: #262626E0 !important

  .note
    font-weight: 600
    margin-top: 0px
    margin-bottom: 0px
    color: #262626E0

  .info-list
    display: flex
    flex-direction: column
    gap: 12px
    margin-top: 8px
    margin-bottom: 16px
    width: 100%
    max-width: 480px

  .info-item
    display: flex
    align-items: flex-start
    gap: 8px
    text-align: left

  .icon
    flex-shrink: 0
    margin-top: 3px
    width: 20px
    height: 20px
    display: flex
    align-items: center
    justify-content: center

  .info-text
    max-width: 452px
    font-weight: 400
    color: #000000A6

  .alert-wrapper
    width: 480px

  :deep(.ant-alert)
    width: 100%
    height: 70px
    text-align: left
    padding-top: 12px
    padding-bottom: 12px

  :deep(.ant-alert-icon)
    transform: translateY(50%)

  :deep(.ant-alert-message)
    margin-bottom: 2px
    font-size: 14px
    font-weight: 600
    line-height: 22px

  .footer
    width: 480px
    margin-top: 16px

  .button
    width: 100%

.whatsapp-business-integration
  height: 100%
  width: 100%
</style>
